import { useState } from "react";
import { Download, FileText, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ExportScope } from "./scope-selection";

interface ExportGenerationProps {
  scope: ExportScope;
  selectedFormat: string;
  onBack: () => void;
  onComplete: () => void;
}

interface ExportResult {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  recordCount?: number;
  error?: string;
}

export const ExportGeneration = ({ scope, selectedFormat, onBack, onComplete }: ExportGenerationProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const generateExport = async () => {
    setIsGenerating(true);
    setExportResult(null);

    try {
      // Fetch transactions for the export
      const { data: transactions, error: fetchError } = await supabase
        .from('transactions')
        .select('*')
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate)
        .order('transaction_date', { ascending: true });

      if (fetchError) throw fetchError;

      // Filter out unclassified if needed
      let filteredTransactions = transactions || [];
      if (!scope.includeUnclassified) {
        filteredTransactions = filteredTransactions.filter(
          t => t.account_number && t.account_number !== ''
        );
      }

      // Generate CSV based on format
      const csvData = generateCSV(filteredTransactions, selectedFormat);

      // Create preview (first 10 rows)
      setPreviewData(filteredTransactions.slice(0, 10));

      // Create blob and download
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const fileName = `export_${selectedFormat}_${scope.startDate}_${scope.endDate}.csv`;

      // Save export record to database
      const { error: insertError } = await supabase
        .from('exports')
        .insert({
          format: selectedFormat,
          start_date: scope.startDate,
          end_date: scope.endDate,
          record_count: filteredTransactions.length,
          file_name: fileName,
          status: 'completed',
          organization_id: (await supabase.auth.getUser()).data.user?.id || '',
          user_id: (await supabase.auth.getUser()).data.user?.id || ''
        });

      if (insertError) {
        console.error('Failed to save export record:', insertError);
      }

      setExportResult({
        success: true,
        fileUrl: url,
        fileName,
        recordCount: filteredTransactions.length
      });

    } catch (error) {
      console.error('Export generation error:', error);
      setExportResult({
        success: false,
        error: error instanceof Error ? error.message : 'Export generation failed'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCSV = (transactions: any[], format: string): string => {
    if (format === 'bexio') {
      return generateBexioCSV(transactions);
    } else if (format === 'datev') {
      return generateDatevCSV(transactions);
    } else {
      return generateStandardCSV(transactions);
    }
  };

  const generateBexioCSV = (transactions: any[]): string => {
    const headers = [
      'Datum',
      'Konto',
      'Soll',
      'Haben',
      'Beschreibung',
      'Beleg-Nr.',
      'MWST-Code'
    ];

    const rows = transactions.map(t => {
      const amount = Math.abs(parseFloat(t.amount?.toString() || '0'));
      const isDebit = parseFloat(t.amount?.toString() || '0') < 0;

      return [
        new Date(t.transaction_date).toLocaleDateString('de-CH'),
        t.account_number || '',
        isDebit ? amount.toFixed(2) : '',
        !isDebit ? amount.toFixed(2) : '',
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.id || '',
        t.vat_code || ''
      ].join(';');
    });

    return [headers.join(';'), ...rows].join('\n');
  };

  const generateDatevCSV = (transactions: any[]): string => {
    const headers = [
      'Umsatz',
      'Soll/Haben-Kennzeichen',
      'Konto',
      'Gegenkonto',
      'Belegdatum',
      'Buchungstext',
      'Belegfeld 1'
    ];

    const rows = transactions.map(t => {
      const amount = Math.abs(parseFloat(t.amount?.toString() || '0'));
      const isDebit = parseFloat(t.amount?.toString() || '0') < 0;

      return [
        amount.toFixed(2),
        isDebit ? 'S' : 'H',
        t.account_number || '',
        '',
        new Date(t.transaction_date).toLocaleDateString('de-DE'),
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.id || ''
      ].join(';');
    });

    return [headers.join(';'), ...rows].join('\n');
  };

  const generateStandardCSV = (transactions: any[]): string => {
    const headers = [
      'Date',
      'Account',
      'Debit',
      'Credit',
      'Description',
      'Reference',
      'Confidence'
    ];

    const rows = transactions.map(t => {
      const amount = Math.abs(parseFloat(t.amount?.toString() || '0'));
      const isDebit = parseFloat(t.amount?.toString() || '0') < 0;

      return [
        t.transaction_date,
        t.account_number || '',
        isDebit ? amount.toFixed(2) : '',
        !isDebit ? amount.toFixed(2) : '',
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.id || '',
        t.confidence_score ? (t.confidence_score * 100).toFixed(1) + '%' : ''
      ].join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  };

  const handleDownload = () => {
    if (exportResult?.fileUrl && exportResult?.fileName) {
      const link = document.createElement('a');
      link.href = exportResult.fileUrl;
      link.download = exportResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-brand-primary mb-2 text-center">
        Generate Export
      </h3>
      <p className="text-brand-text-secondary text-sm mb-6 text-center">
        Create and download your export file
      </p>

      <div className="max-w-2xl mx-auto">
        {/* Export Summary */}
        <div className="bg-white p-6 rounded-xl border-2 border-brand-border mb-6">
          <h4 className="font-bold text-brand-text-primary mb-4">Export Configuration</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-brand-text-secondary">Format:</span>
              <span className="font-medium text-brand-text-primary">{selectedFormat.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-text-secondary">Date Range:</span>
              <span className="font-medium text-brand-text-primary">
                {new Date(scope.startDate).toLocaleDateString('de-CH')} - {new Date(scope.endDate).toLocaleDateString('de-CH')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-text-secondary">Include Unclassified:</span>
              <span className="font-medium text-brand-text-primary">
                {scope.includeUnclassified ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        {!exportResult && (
          <div className="text-center mb-6">
            <button
              onClick={generateExport}
              disabled={isGenerating}
              className="px-8 py-4 bg-brand-primary text-white rounded-xl font-bold text-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Generating Export...
                </>
              ) : (
                <>
                  <FileText className="h-6 w-6" />
                  Generate Export File
                </>
              )}
            </button>
          </div>
        )}

        {/* Export Result */}
        {exportResult && (
          <div className={`p-6 rounded-xl border-2 mb-6 ${
            exportResult.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {exportResult.success ? (
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1">
                {exportResult.success ? (
                  <>
                    <h4 className="font-bold text-green-900 mb-2">Export Generated Successfully!</h4>
                    <p className="text-sm text-green-800 mb-4">
                      {exportResult.recordCount} transactions exported to {exportResult.fileName}
                    </p>
                    <button
                      onClick={handleDownload}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <Download className="h-5 w-5" />
                      Download Export File
                    </button>
                  </>
                ) : (
                  <>
                    <h4 className="font-bold text-red-900 mb-2">Export Failed</h4>
                    <p className="text-sm text-red-800">{exportResult.error}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        {previewData.length > 0 && (
          <div className="bg-white p-6 rounded-xl border border-brand-border">
            <h4 className="font-bold text-brand-text-primary mb-4">Preview (First 10 Rows)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-secondary">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-brand-text-primary">Date</th>
                    <th className="px-3 py-2 text-left font-medium text-brand-text-primary">Account</th>
                    <th className="px-3 py-2 text-right font-medium text-brand-text-primary">Amount</th>
                    <th className="px-3 py-2 text-left font-medium text-brand-text-primary">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {previewData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-brand-secondary/50">
                      <td className="px-3 py-2 text-brand-text-secondary">
                        {new Date(row.transaction_date).toLocaleDateString('de-CH')}
                      </td>
                      <td className="px-3 py-2 text-brand-text-primary font-medium">
                        {row.account_number || '-'}
                      </td>
                      <td className="px-3 py-2 text-right text-brand-text-primary font-medium">
                        CHF {parseFloat(row.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-brand-text-secondary truncate max-w-xs">
                        {row.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={onBack}
          disabled={isGenerating}
          className="px-6 py-3 bg-brand-secondary text-brand-primary rounded-lg font-medium hover:bg-brand-secondary/80 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        {exportResult?.success && (
          <button
            onClick={onComplete}
            className="px-6 py-3 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 transition-colors"
          >
            Complete & View History
          </button>
        )}
      </div>
    </div>
  );
};
